import React, { useState } from 'react'
import {
  AppBar, Avatar, Box, Button, Divider, Drawer, IconButton, List,
  ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Toolbar,
  Tooltip, Typography,
} from '@mui/material'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import { canAccessEmergency } from '../data/turkeyProvinces'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import ProfileIcon from '@mui/icons-material/AccountCircle'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import DescriptionIcon from '@mui/icons-material/Description'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import PeopleIcon from '@mui/icons-material/People'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const colors = {
  navy: '#18354a',
  blue: '#347fb1',
  paleBlue: '#eaf4fa',
  border: '#dce7ee',
  muted: '#6b7f8d',
}

function Navbar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser, logout, userRole, userStatus } = useAuth()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [profileAnchor, setProfileAnchor] = useState(null)
  const [populiAnchor, setPopuliAnchor] = useState(null)

  const hasAccess = userRole === 'user' || userRole === 'admin'
  const isAdmin = userRole === 'admin'
  const primaryItems = [
    { text: 'Ana Sayfa', icon: <DescriptionIcon />, path: '/ProjectsPage', visible: hasAccess },
    { text: 'Acil Durum', icon: <CampaignOutlinedIcon />, path: '/emergency', visible: canAccessEmergency(currentUser, userStatus) },
    { text: 'Gönderim Raporu', icon: <AssessmentIcon />, path: '/emergency/reports', visible: canAccessEmergency(currentUser, userStatus) },
    { text: 'Destek Talepleri', icon: <AssignmentOutlinedIcon />, path: '/emergency/support-requests', visible: canAccessEmergency(currentUser, userStatus) },
    { text: 'Kullanıcı Yönetimi', icon: <ManageAccountsIcon />, path: '/admin-users', visible: isAdmin },
  ]
  const populiItems = [
    { text: 'Populi Dokümanları', icon: <DescriptionIcon />, path: '/populi/documents', visible: hasAccess },
    { text: 'Populi Raporları', icon: <AssessmentIcon />, path: '/populi/reports', visible: hasAccess },
    { text: 'Populi Kullanıcıları', icon: <PeopleIcon />, path: '/populi/users', visible: hasAccess },
    { text: 'Populi Demo', icon: <SmartToyIcon />, path: '/populi/demo', visible: hasAccess },
  ]
  const visiblePopuliItems = populiItems.filter((item) => item.visible)
  const isActive = (path) => location.pathname === path
  const isLoginPage = isActive('/login')
  const populiActive = populiItems.some((item) => isActive(item.path))

  const goTo = (path) => {
    navigate(path)
    setDrawerOpen(false)
    setPopuliAnchor(null)
    setProfileAnchor(null)
  }

  const handleLogout = async () => {
    setProfileAnchor(null)
    setDrawerOpen(false)
    navigate('/login')
    try {
      await logout()
    } catch (error) {
      console.error('Çıkış yapılamadı:', error)
    }
  }

  const navButtonSx = (active) => ({
    minHeight: 40,
    px: 1.75,
    borderRadius: 2,
    color: active ? colors.navy : colors.muted,
    bgcolor: active ? colors.paleBlue : 'transparent',
    fontWeight: active ? 800 : 700,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { bgcolor: colors.paleBlue, color: colors.navy },
  })

  const drawerItemSx = (active) => ({
    mx: 1.5,
    mb: 0.5,
    px: 1.5,
    minHeight: 46,
    borderRadius: 2,
    color: active ? colors.navy : colors.muted,
    bgcolor: active ? `${colors.blue}14` : 'transparent',
    borderLeft: `3px solid ${active ? colors.blue : 'transparent'}`,
    '&:hover': { bgcolor: colors.paleBlue, color: colors.navy },
    '& .MuiListItemIcon-root': { minWidth: 38, color: active ? colors.blue : colors.muted },
    '& .MuiListItemText-primary': { fontSize: 14, fontWeight: active ? 800 : 650 },
  })

  const menuPaperSx = {
    mt: 1,
    p: 0.75,
    borderRadius: 2.5,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 18px 48px rgba(24, 53, 74, 0.16)',
  }

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,.96)',
          color: colors.navy,
          borderBottom: `1px solid ${colors.border}`,
          boxShadow: '0 8px 28px rgba(24,53,74,.07)',
          backdropFilter: 'blur(12px)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ width: '100%', maxWidth: 1440, minHeight: { xs: 64, md: 72 }, mx: 'auto', px: { xs: 2, md: 3 }, gap: { xs: 1, md: 2 } }}>
          <IconButton aria-label="Menüyü aç" onClick={() => setDrawerOpen(true)} sx={{ display: { md: 'none' }, color: colors.navy }}>
            <MenuIcon />
          </IconButton>

          <Box
            component="button"
            type="button"
            onClick={() => goTo('/ProjectsPage')}
            aria-label="Populi ana sayfa"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 0, mr: { md: 1 }, bgcolor: 'transparent', border: 0, cursor: 'pointer' }}
          >
            <Box component="img" src="/logo.png" alt="Populi" sx={{ width: { xs: 54, md: 62 }, height: 'auto', display: 'block' }} />
            <Box sx={{ display: { xs: 'none', lg: 'block' }, textAlign: 'left' }}>
              <Typography sx={{ color: colors.navy, fontSize: 16, fontWeight: 900, lineHeight: 1.1 }}>Populi</Typography>
              <Typography sx={{ color: colors.muted, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5 }}>DOCUMENT INTELLIGENCE</Typography>
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, flex: 1 }}>
            {primaryItems.filter((item) => item.visible).map((item) => (
              <Button key={item.path} onClick={() => goTo(item.path)} sx={navButtonSx(isActive(item.path))}>
                {item.text}
              </Button>
            ))}
            {visiblePopuliItems.length > 0 && (
              <Button
                onClick={(event) => setPopuliAnchor(event.currentTarget)}
                endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 160ms', transform: populiAnchor ? 'rotate(180deg)' : 'none' }} />}
                sx={navButtonSx(populiActive)}
              >
                Populi
              </Button>
            )}
          </Box>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentUser ? (
              <Tooltip title="Profil" arrow>
                <Button
                  onClick={(event) => setProfileAnchor(event.currentTarget)}
                  endIcon={<KeyboardArrowDownIcon sx={{ color: colors.muted }} />}
                  sx={{ p: 0.5, pr: { xs: 0.5, lg: 1 }, minWidth: 0, borderRadius: 3, textTransform: 'none', '&:hover': { bgcolor: colors.paleBlue } }}
                >
                  <Avatar
                    src={currentUser.avatarUrl || undefined}
                    alt={currentUser.nickname || currentUser.email || 'Kullanıcı'}
                    sx={{ width: 38, height: 38, bgcolor: colors.navy, color: '#fff', fontSize: 15, fontWeight: 800 }}
                  >
                    {(currentUser.nickname || currentUser.email || 'K').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ display: { xs: 'none', lg: 'block' }, ml: 1, maxWidth: 135, textAlign: 'left' }}>
                    <Typography noWrap sx={{ color: colors.navy, fontSize: 13, fontWeight: 800, lineHeight: 1.25 }}>
                      {currentUser.nickname || currentUser.displayName || 'Profil'}
                    </Typography>
                    <Typography noWrap sx={{ color: colors.muted, fontSize: 10.5, lineHeight: 1.25 }}>{currentUser.email}</Typography>
                  </Box>
                </Button>
              </Tooltip>
            ) : !isLoginPage ? (
              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={() => goTo('/login')}
                sx={{ bgcolor: colors.navy, borderRadius: 2, px: 2, fontWeight: 800, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: colors.blue, boxShadow: 'none' } }}
              >
                Giriş Yap
              </Button>
            ) : null}
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={populiAnchor}
        open={Boolean(populiAnchor)}
        onClose={() => setPopuliAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { ...menuPaperSx, minWidth: 238 } } }}
      >
        {visiblePopuliItems.map((item) => (
          <MenuItem
            key={item.path}
            selected={isActive(item.path)}
            onClick={() => goTo(item.path)}
            sx={{ minHeight: 46, borderRadius: 1.5, gap: 1.25, color: isActive(item.path) ? colors.navy : colors.muted, fontSize: 14, fontWeight: isActive(item.path) ? 800 : 650, '&.Mui-selected, &.Mui-selected:hover': { bgcolor: colors.paleBlue } }}
          >
            <Box sx={{ display: 'flex', color: isActive(item.path) ? colors.blue : colors.muted }}>{item.icon}</Box>
            {item.text}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { ...menuPaperSx, minWidth: 210 } } }}
      >
        <MenuItem onClick={() => goTo('/profile')} sx={{ minHeight: 44, borderRadius: 1.5, gap: 1.25, color: colors.navy, fontWeight: 700 }}>
          <ProfileIcon sx={{ color: colors.blue }} />Profil
        </MenuItem>
        <Divider sx={{ my: 0.75 }} />
        <MenuItem onClick={handleLogout} sx={{ minHeight: 44, borderRadius: 1.5, gap: 1.25, color: '#b44b4b', fontWeight: 800 }}>
          <LogoutIcon />Çıkış Yap
        </MenuItem>
      </Menu>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 292, bgcolor: '#f8fafb', borderRight: `1px solid ${colors.border}` } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.25, minHeight: 64, bgcolor: '#fff' }}>
          <Box component="img" src="/logo.png" alt="Populi" sx={{ width: 58, height: 'auto' }} />
          <IconButton aria-label="Menüyü kapat" onClick={() => setDrawerOpen(false)} sx={{ color: colors.muted }}><CloseIcon /></IconButton>
        </Box>
        <Divider />

        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 65px)' }}>
          <List sx={{ pt: 2 }}>
            {primaryItems.filter((item) => item.visible).map((item) => (
              <ListItemButton key={item.path} onClick={() => goTo(item.path)} sx={drawerItemSx(isActive(item.path))}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
            {visiblePopuliItems.length > 0 && (
              <>
                <Typography sx={{ mt: 2.5, mb: 1, px: 3, color: colors.muted, fontSize: 10.5, fontWeight: 900, letterSpacing: 1.1 }}>
                  POPULI
                </Typography>
                {visiblePopuliItems.map((item) => (
                  <ListItemButton key={item.path} onClick={() => goTo(item.path)} sx={drawerItemSx(isActive(item.path))}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                ))}
              </>
            )}
          </List>

          <Box sx={{ mt: 'auto', p: 2 }}>
            <Divider sx={{ mb: 2 }} />

            {currentUser ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                  <Avatar src={currentUser.avatarUrl || undefined} sx={{ width: 38, height: 38, bgcolor: colors.navy, fontSize: 14, fontWeight: 800 }}>
                    {(currentUser.nickname || currentUser.email || 'K').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ color: colors.navy, fontSize: 13, fontWeight: 800 }}>{currentUser.nickname || currentUser.displayName || 'Profil'}</Typography>
                    <Typography noWrap sx={{ color: colors.muted, fontSize: 10.5 }}>{currentUser.email}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button fullWidth variant="outlined" startIcon={<ProfileIcon />} onClick={() => goTo('/profile')} sx={{ borderColor: colors.border, color: colors.navy, textTransform: 'none', fontWeight: 750 }}>
                    Profil
                  </Button>
                  <Tooltip title="Çıkış Yap" arrow>
                    <IconButton onClick={handleLogout} sx={{ border: '1px solid #ead3d3', color: '#b44b4b', borderRadius: 2 }}><LogoutIcon /></IconButton>
                  </Tooltip>
                </Box>
              </>
            ) : !isLoginPage ? (
              <Button fullWidth variant="contained" startIcon={<LoginIcon />} onClick={() => goTo('/login')} sx={{ bgcolor: colors.navy, boxShadow: 'none', textTransform: 'none', fontWeight: 800 }}>
                Giriş Yap
              </Button>
            ) : null}
          </Box>
        </Box>
      </Drawer>
    </>
  )
}

export default Navbar
